"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { useDoctorData } from "@/hooks/useDoctorData";
import { getStoredDoctorUnitSystem, persistDoctorUnitSystem, resolveDoctorUnitSystem, } from "@/lib/doctor-units";
import { formatHeight, formatPortion, formatWeight, formatWeightValue, heightSuffix, portionSuffix, weightSuffix, } from "@/lib/units";
const UnitSystemContext = createContext(null);
const METRIC_FALLBACK = {
    system: "metric",
    setSystem: () => { },
    weight: (kg) => formatWeight(kg, "metric"),
    weightValue: (kg) => formatWeightValue(kg, "metric"),
    height: (cm) => formatHeight(cm, "metric"),
    weightUnit: weightSuffix("metric"),
    heightUnit: heightSuffix("metric"),
    portion: (grams) => formatPortion(grams, "metric"),
    portionSuffix: portionSuffix("metric"),
};
export function UnitSystemProvider({ children }) {
    const { data: doctorData } = useDoctorData();
    const [system, setSystemState] = useState("metric");
    // The device choice is read after mount so server and client render the same
    // markup; the backend value fills in for a machine that has none stored.
    useEffect(() => {
        setSystemState(resolveDoctorUnitSystem(getStoredDoctorUnitSystem(), doctorData === null || doctorData === void 0 ? void 0 : doctorData.unit_system));
    }, [doctorData]);
    const setSystem = useCallback((next) => {
        persistDoctorUnitSystem(next);
        setSystemState(next);
    }, []);
    const value = useMemo(() => ({
        system,
        setSystem,
        weight: (kg) => formatWeight(kg, system),
        weightValue: (kg) => formatWeightValue(kg, system),
        height: (cm) => formatHeight(cm, system),
        weightUnit: weightSuffix(system),
        heightUnit: heightSuffix(system),
        portion: (grams) => formatPortion(grams, system),
        portionSuffix: portionSuffix(system),
    }), [system, setSystem]);
    return (_jsx(UnitSystemContext.Provider, { value: value, children: children }));
}
/**
 * Renders canonical values in the doctor's chosen system.
 *
 * Outside a provider it reports metric, which is what the stored values already
 * are — a component that misses the provider shows the unconverted number
 * rather than a wrong one.
 */
export function useUnitSystem() {
    var _a;
    return (_a = useContext(UnitSystemContext)) !== null && _a !== void 0 ? _a : METRIC_FALLBACK;
}
